import { useEffect, useMemo, useRef, useState } from "react";
import { getSpecieVolume } from "@foresting-tomorrow/simulation";

// Project import
import useMapData from "./useMapData";
import { FirestoreActivityProps } from "types/firestoreFeatures";
import useSpeciesData from "./useSpeciesData";
import { EstimationModels } from "types/simulation";
import { ThinningVolumeSummary } from "types/boards";
import { roundWithPrecision } from "utils/numberModification";

const useThinningData = (activityList: FirestoreActivityProps[]) => {
  // --- Hooks --- //
  const {
    standData,
    activityFeatures,
    thinningData,
    fetchStandData,
    fetchThinningData,
    fetchActivityFeatures,
  } = useMapData();
  const { getSpeciesModelType, getSpeciesNameAndAbbreviation } = useSpeciesData();

  // --- State and references --- //
  const [thinningVolume, setThinningVolume] = useState<ThinningVolumeSummary | null>(null);
  const [speciesAndPercentage, setSpeciesAndPercentage] = useState<{id: string, percentage: number}[]>([]);

  const activityListRef = useRef(activityList);
  const thinningDataRef = useRef(thinningData);
  const standDataRef = useRef(standData);
  const activityFeaturesRef = useRef(activityFeatures);

  // --- Effects --- //
  thinningDataRef.current = useMemo(() => {
    return thinningData;
  }, [JSON.stringify(thinningData)]);
  standDataRef.current = useMemo(() => {
    return standData;
  }, [JSON.stringify(standData)]);
  activityFeaturesRef.current = useMemo(() => {
    return activityFeatures;
  }, [JSON.stringify(activityFeatures)]);

  activityListRef.current = useMemo(() => {
    return activityList;
  }, [JSON.stringify(activityList)]);

  // Calculate new values on data updates
  useEffect(() => {
    getThinningVolumeFromActivitiesList();
  }, [
    activityListRef.current,
    thinningDataRef.current,
    standDataRef.current,
    activityFeaturesRef.current,
  ]);

  // --- Functions --- //
  const getThinningVolumeFromActivitiesList = () => {
    const volumes: ThinningVolumeSummary = {};
    const speciesData: {id: string, percentage: number}[] = [];
    for (const activity of activityListRef.current) {
      // Extract references
      const { id: activityId, type, mapFeatureRefs } = activity;
      // Check for activity type group (only logging results in volume)
      if (type?.group !== "logging" || !mapFeatureRefs) continue;
      // Run through mapFeatureRefs
      for (const ref of mapFeatureRefs) {
        // Split ref into map and feature id
        const splitRef = ref.split("/");
        const mapId = splitRef[0];
        const featureId = splitRef[1];
        // Check for correct map and feature id
        if (!mapId || !featureId) continue;
        // Check if stand data for Map Id exists in reducer. If not fetch it
        if (!standDataRef.current[mapId]) {
          fetchStandData(mapId);
        }
        if (!activityFeaturesRef.current[activityId]) {
          fetchActivityFeatures(activityId);
        }
        if (!thinningDataRef.current[mapId]) {
          fetchThinningData(mapId, "mapId");
          continue;
        }
        // Find feature metric for activity
        let featureMetric: number = 1;
        const activityFeature = activityFeaturesRef.current[activityId]
          ? Object.values(activityFeaturesRef.current[activityId]).find((feat) => {
              return feat.properties?.originsFeatureId;
            })
          : null;
        if (activityFeature) {
          featureMetric = activityFeature.properties?.originsFeatureMetric
            ? activityFeature.properties.originsFeatureMetric
            : 1;
        }
        // Find feature stands
        const featureStands = standDataRef.current[mapId].filter(
          (el) => el.featureId === featureId
        );
        // Get thinning data for mapId
        const thinningDataObj = thinningDataRef.current[mapId];
        // Run through feature stands
        for (const stand of featureStands) {
          // Get relevant stand data
          const { id: standId, species, diameter, height, treeCount } = stand;
          // Find thinning data for stand and activityId
          const standThinning = thinningDataObj
            ? Object.values(thinningDataObj).find(
                (el) => el.standId === standId && el.activityId === activityId
              )
            : null;
          if (!standThinning || !standThinning.percentageThinning) continue;
          // Get species name and abbreviation
          speciesData.push({
            id: species,
            percentage: standThinning.percentageThinning,
          });
          if (diameter && diameter > 0 && height && height > 0 && treeCount && treeCount > 0) {
            const volume =
              getSpecieVolume(getSpeciesModelType(species) as EstimationModels, {
                d: diameter / 100,
                h: height,
                T: 0,
                a: 0,
              }).vb *
              treeCount *
              featureMetric *
              (standThinning.percentageThinning / 100);
            if (volume > 0) {
              if (!volumes[activityId]) {
                volumes[activityId] = {};
              }
              volumes[activityId][standId] = roundWithPrecision(volume, 0);
            }
          }
        }
      }
    }
    setThinningVolume(volumes);
    setSpeciesAndPercentage(speciesData);
  };

  return {
    thinningData,
    thinningVolume,
    speciesAndPercentage,
  };
};

export default useThinningData;
